// src/features/auth/pages/ForceColorPickerPage.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Container,
  Tooltip,
} from '@mui/material';
import { Palette as PaletteIcon } from '@mui/icons-material';
import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

const PRESET_COLORS = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1',
  '#1E88E5', '#00ACC1', '#00897B', '#43A047',
  '#F4511E', '#FB8C00', '#F9A825', '#6D4C41',
  '#546E7A', '#757575', '#EC407A', '#7CB342',
];

export default function ForceColorPickerPage() {
  const { t } = useTranslation(['staff', 'common']);
  const location = useLocation();
  const { showSuccess } = useSnackbar();
  const { setColor } = useStaff();

  const requiresSessionRefresh = location.state?.requiresSessionRefresh ?? false;

  const [selectedColor, setSelectedColor] = useState('#1E88E5');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const finishAndRedirect = () => {
    if (requiresSessionRefresh) {
      window.location.href = '/home';
    } else {
      window.location.href = '/home';
    }
  };

  const handleSave = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await setColor(selectedColor);
      if (result.success) {
        showSuccess(t('staff:profile.color_set_success'));
        finishAndRedirect();
      } else {
        setError(result.error?.message || t('staff:profile.color_set_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    await setColor('#151632');
    finishAndRedirect();
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        py={4}
      >
        <Box textAlign="center" mb={4}>
          <PaletteIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('staff:profile.color_picker_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('staff:profile.color_picker_message')}
          </Typography>
        </Box>

        <Card sx={{ width: '100%' }}>
          <CardContent>
            <Stack spacing={3}>
              {/* Color preview */}
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: selectedColor,
                    border: '3px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('staff:profile.selected_color')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" fontFamily="monospace">
                    {selectedColor.toUpperCase()}
                  </Typography>
                </Box>
                {/* Native color input overlaid on a styled button */}
                <Tooltip title={t('staff:profile.pick_custom_color')}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
                    }}
                  >
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={(e) => setSelectedColor(e.target.value)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        border: 'none',
                        padding: 0,
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>

              {/* Preset swatches */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('staff:profile.preset_colors')}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {PRESET_COLORS.map((color) => (
                    <Tooltip key={color} title={color.toUpperCase()}>
                      <Box
                        onClick={() => setSelectedColor(color)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: color,
                          cursor: 'pointer',
                          border: '3px solid',
                          borderColor: selectedColor === color ? 'text.primary' : 'transparent',
                          transition: 'transform 0.1s, border-color 0.1s',
                          '&:hover': { transform: 'scale(1.15)' },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>

              {error && (
                <Alert severity="error">{error}</Alert>
              )}

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={handleSkip}
                  disabled={loading}
                >
                  {t('common:buttons.skip')}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSave}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <PaletteIcon />}
                >
                  {loading ? t('common:buttons.saving') : t('staff:profile.save_color')}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
